import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useMemo, useState } from 'react';
import { GeoJSON, MapContainer, TileLayer, useMap } from 'react-leaflet';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { COUNTY_FIPS_TO_NAME } from '../../utils/countyFipsMapping';

delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: string })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

type UhiTractRow = {
  geofips: string | number;
  region_name?: string | null;
  uhii_percentile?: string | number | null;
  deg_hour_day?: string | number | null;
};

interface UrbanHeatIslandMapProps {
  selectedLocation: string;
}

const TRACT_GEOJSON_URL = `${process.env.PUBLIC_URL || ''}/data/california_tracts_optimized.geojson`;

const CALIFORNIA_CENTER: [number, number] = [36.7783, -119.4179];
const CALIFORNIA_ZOOM = 6;

// Match Owner-Occupied Housing Map palette (green -> yellow -> red), sampled into 5 bins.
const BIN_COLORS = ['#1a9850', '#a6d96a', '#fee08b', '#f46d43', '#b2182b'];
const NO_DATA_COLOR = '#e5e7eb';

const NAME_TO_COUNTY_FIPS = Object.fromEntries(
  Object.entries(COUNTY_FIPS_TO_NAME).map(([fips, name]) => [normalizeCountyName(name), fips])
) as Record<string, string>;

function normalizeCountyName(value: string): string {
  return value
    .toLowerCase()
    .replace(/\s+county$/i, '')
    .replace(/[^a-z0-9]+/g, '')
    .trim();
}

function normalizeLocationName(value: string): string {
  return value
    .toLowerCase()
    .replace(/,\s*ca$/i, '')
    .replace(/\s+city$/i, '')
    .replace(/\s+county$/i, '')
    .replace(/[^a-z0-9]+/g, '')
    .trim();
}

function isCaliforniaLocation(value: string): boolean {
  const normalized = normalizeLocationName(value);
  return normalized === 'california' || normalized === 'stateofcalifornia';
}

function toTractGeofips(value: string | number | null | undefined): string {
  if (value == null) return '';
  const digits = String(value).replace(/\D/g, '');
  if (!digits || digits.length > 11) return '';
  return digits.padStart(11, '0');
}

function toFiniteNumber(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatPercentile(value?: string | number | null): string {
  const num = toFiniteNumber(value);
  if (num == null) return 'N/A';
  return `${num.toFixed(1)}`;
}

function formatDegHourDay(value?: string | number | null): string {
  const num = toFiniteNumber(value);
  if (num == null) return 'N/A';
  return num.toFixed(2);
}

function getPercentileColor(percentile?: string | number | null): string {
  const num = toFiniteNumber(percentile);
  if (num == null) return NO_DATA_COLOR;
  if (num < 20) return BIN_COLORS[0];
  if (num < 40) return BIN_COLORS[1];
  if (num < 60) return BIN_COLORS[2];
  if (num < 80) return BIN_COLORS[3];
  return BIN_COLORS[4];
}

async function fetchAllRows(table: string, select: string, filters: Array<[string, string]> = []): Promise<any[]> {
  const pageSize = 1000;
  let offset = 0;
  const allRows: any[] = [];
  while (true) {
    let query = supabaseClient.from(table).select(select).range(offset, offset + pageSize - 1);
    filters.forEach(([column, value]) => {
      query = query.eq(column, value);
    });
    const { data, error } = await query;
    if (error) throw error;
    const rows = data || [];
    allRows.push(...rows);
    if (rows.length < pageSize) break;
    offset += pageSize;
  }
  return allRows;
}

function FitMapToColoredTracts({
  geoJsonData,
  rowByGeofips,
  getFeatureGeofipsKey,
}: {
  geoJsonData: any;
  rowByGeofips: Map<string, UhiTractRow>;
  getFeatureGeofipsKey: (properties: Record<string, any>) => string;
}) {
  const map = useMap();

  useEffect(() => {
    if (!geoJsonData?.features?.length) return;

    const bounds = L.latLngBounds([]);
    for (const feature of geoJsonData.features) {
      const props = (feature?.properties || {}) as Record<string, any>;
      const key = getFeatureGeofipsKey(props);
      const row = rowByGeofips.get(key);
      const hasData = row != null && toFiniteNumber(row.uhii_percentile) != null;
      if (!hasData) continue;
      try {
        const featureBounds = L.geoJSON(feature as any).getBounds();
        if (featureBounds.isValid()) bounds.extend(featureBounds);
      } catch {
        // Ignore malformed features.
      }
    }

    if (!bounds.isValid()) return;
    map.fitBounds(bounds, { padding: [20, 20], maxZoom: 11 });
  }, [map, geoJsonData, rowByGeofips, getFeatureGeofipsKey]);

  return null;
}

const UrbanHeatIslandMap: React.FC<UrbanHeatIslandMapProps> = ({ selectedLocation }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [geoJsonData, setGeoJsonData] = useState<any>(null);
  const [tractRows, setTractRows] = useState<UhiTractRow[]>([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await supabaseLocationService.initialize();
      } catch {
        // Best-effort initialization; fallback location handling still works.
      }
      if (!mounted) return;
      setError(null);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadGeoJson = async () => {
      try {
        const response = await fetch(TRACT_GEOJSON_URL);
        if (!response.ok) throw new Error(`Failed to load map boundaries (HTTP ${response.status})`);
        const data = await response.json();
        if (!cancelled) setGeoJsonData(data);
      } catch (err) {
        if (!cancelled) {
          setGeoJsonData(null);
          setError(err instanceof Error ? err.message : 'Failed to load map boundaries');
        }
      }
    };
    loadGeoJson();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadDataByGeography = async () => {
      setLoading(true);
      setError(null);
      try {
        if (tractRows.length === 0) {
          const rows = (await fetchAllRows(
            'urban_heat_island_tract',
            'geofips, region_name, uhii_percentile, deg_hour_day'
          )) as UhiTractRow[];
          if (!cancelled) setTractRows(rows);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load urban heat island data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadDataByGeography();
    return () => {
      cancelled = true;
    };
  }, [tractRows.length]);

  const locationContext = useMemo(() => {
    const raw = selectedLocation || 'California';
    const locationType = supabaseLocationService.getLocationType(raw);
    const countyNameFromService = supabaseLocationService.getCountyForCity(raw);
    const normalizedCountyFromService = countyNameFromService ? normalizeCountyName(countyNameFromService) : null;
    const normalizedLocation = normalizeLocationName(raw);
    const normalizedCountyFromLocation = normalizeCountyName(raw);
    const countyKey =
      normalizedCountyFromService ||
      (locationType === 'county' ? normalizedCountyFromLocation : null) ||
      (raw.toLowerCase().endsWith(' county') ? normalizedCountyFromLocation : null);
    return {
      raw,
      locationType,
      normalizedLocation,
      countyKey,
      isCalifornia: isCaliforniaLocation(raw),
    };
  }, [selectedLocation]);

  const filteredRows = useMemo(() => {
    if (locationContext.isCalifornia || !locationContext.countyKey) return tractRows;
    const countyFips = NAME_TO_COUNTY_FIPS[locationContext.countyKey];
    if (!countyFips) return tractRows;
    return tractRows.filter((row) => {
      const tractFips = String(row.geofips ?? '').padStart(11, '0').slice(2, 5);
      return tractFips === countyFips;
    });
  }, [tractRows, locationContext]);

  const rowByGeofips = useMemo(() => {
    const map = new Map<string, UhiTractRow>();
    filteredRows.forEach((row) => {
      const key = toTractGeofips(row.geofips);
      if (key) map.set(key, row);
    });
    return map;
  }, [filteredRows]);

  const getFeatureGeofipsKey = useMemo(
    () => (properties: Record<string, any>) => toTractGeofips(properties?.GEOID),
    []
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ color: '#475569', fontSize: 13 }}>
          Showing tract-level UHII percentile for {selectedLocation || 'California'}
        </span>
      </div>

      {error && (
        <div style={{ marginBottom: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', padding: 10 }}>
          {error}
        </div>
      )}

      {loading && <p style={{ margin: 0, color: '#475569' }}>Loading urban heat island map data…</p>}

      {!loading && geoJsonData && (
        <div style={{ height: 560, width: '100%', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          <MapContainer center={CALIFORNIA_CENTER} zoom={CALIFORNIA_ZOOM} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <GeoJSON
              data={geoJsonData}
              style={(feature) => {
                const props = (feature?.properties || {}) as Record<string, any>;
                const geofipsKey = getFeatureGeofipsKey(props);
                const row = rowByGeofips.get(geofipsKey);
                const hasData = row != null && toFiniteNumber(row.uhii_percentile) != null;
                if (!hasData) {
                  return {
                    fillColor: 'transparent',
                    weight: 0,
                    opacity: 0,
                    color: 'transparent',
                    fillOpacity: 0,
                  };
                }
                return {
                  fillColor: getPercentileColor(row?.uhii_percentile ?? null),
                  weight: 0.6,
                  opacity: 1,
                  color: '#ffffff',
                  fillOpacity: 0.8,
                };
              }}
              onEachFeature={(feature, layer) => {
                const props = (feature?.properties || {}) as Record<string, any>;
                const geofipsKey = getFeatureGeofipsKey(props);
                const row = rowByGeofips.get(geofipsKey);
                const hasData = row != null && toFiniteNumber(row.uhii_percentile) != null;
                if (!hasData) return;
                const finalName =
                  row?.region_name ||
                  props?.NAME ||
                  props?.NAMELSAD ||
                  props?.region_name ||
                  'Unknown geography';
                layer.bindTooltip(
                  `
                  <div style="font-size:12px;line-height:1.4;">
                    <div style="font-weight:600;margin-bottom:4px;">${finalName}</div>
                    <div>UHII Percentile: ${formatPercentile(row?.uhii_percentile)}</div>
                    <div>Degree Hour Day: ${formatDegHourDay(row?.deg_hour_day)}</div>
                  </div>
                  `,
                  { sticky: true }
                );
              }}
            />
            <FitMapToColoredTracts
              geoJsonData={geoJsonData}
              rowByGeofips={rowByGeofips}
              getFeatureGeofipsKey={getFeatureGeofipsKey}
            />
          </MapContainer>
        </div>
      )}

      <div style={{ marginTop: 12, color: '#334155' }}>
        <strong style={{ display: 'block', marginBottom: 6 }}>UHII Percentile</strong>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {[
            { label: '0-20 (Lowest)', color: BIN_COLORS[0] },
            { label: '20-40', color: BIN_COLORS[1] },
            { label: '40-60', color: BIN_COLORS[2] },
            { label: '60-80', color: BIN_COLORS[3] },
            { label: '80-100 (Highest)', color: BIN_COLORS[4] },
          ].map((bin) => (
            <div key={bin.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  width: 18,
                  height: 12,
                  display: 'inline-block',
                  backgroundColor: bin.color,
                  border: '1px solid #cbd5e1',
                }}
              />
              <span style={{ fontSize: 12 }}>{bin.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UrbanHeatIslandMap;
